import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings, Send, CheckCircle, XCircle, 
  RefreshCw, Phone, MessageSquare, Building2, Eye, EyeOff
} from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
];

export default function SuperAdminSMS() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [smsConfig, setSmsConfig] = useState({
    enabled: false,
    provider: 'twilio',
    account_sid: '',
    auth_token: '',
    sender_phone: '',
    timezone: 'America/Denver'
  });
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const tenantsRes = await axios.get(`${API}/platform/tenants`, getAuthConfig());
      setTenants(tenantsRes.data || []);
      
      try {
        const configRes = await axios.get(`${API}/platform/sms-config`, getAuthConfig());
        if (configRes.data) setSmsConfig(configRes.data);
      } catch (e) { /* Config may not exist yet */ }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/sms-config`, smsConfig, getAuthConfig());
      toast.success('SMS configuration saved');
      setShowConfigDialog(false);
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestSMS = async () => {
    if (!testPhone || !testMessage) {
      toast.error('Enter phone and message');
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/platform/sms/test`, { phone: testPhone, message: testMessage }, getAuthConfig());
      toast.success('Test SMS sent!');
      setTestPhone('');
      setTestMessage('');
    } catch (error) {
      toast.error('Failed to send test SMS');
    } finally {
      setSending(false);
    }
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">SMS</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Messaging config</p>
          </div>
          <Button size="sm" onClick={() => setShowConfigDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0">
            <Settings className="w-4 h-4 lg:mr-1" />
            <span className="hidden lg:inline">Configure</span>
          </Button>
        </div>

        {/* Status Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${smsConfig.enabled ? 'bg-emerald-900' : 'bg-zinc-800'}`}>
                <Phone className={`w-6 h-6 ${smsConfig.enabled ? 'text-emerald-400' : 'text-zinc-400'}`} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">SMS Service</h2>
                <div className="flex items-center gap-2">
                  {smsConfig.enabled ? (
                    <Badge className="bg-emerald-600 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-zinc-400 text-xs"><XCircle className="w-3 h-3 mr-1" />Not Configured</Badge>
                  )}
                  {smsConfig.provider && <span className="text-xs text-zinc-500">via {smsConfig.provider}</span>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="tenants" className="space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800 w-full justify-start">
            <TabsTrigger value="tenants" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm">
              <Building2 className="w-4 h-4 mr-1 sm:mr-2" /> Tenants
            </TabsTrigger>
            <TabsTrigger value="test" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm">
              <Send className="w-4 h-4 mr-1 sm:mr-2" /> Test
            </TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Tenant SMS Status</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-zinc-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading...
                  </div>
                ) : tenants.length === 0 ? (
                  <p className="text-zinc-400 text-sm text-center py-8">No tenants yet</p>
                ) : (
                  <div className="space-y-2">
                    {tenants.map(tenant => (
                      <div key={tenant.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                          <span className="text-white text-sm truncate">{tenant.name}</span>
                        </div>
                        <Badge variant="outline" className="text-zinc-400 text-xs flex-shrink-0">
                          {tenant.sms_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Send Test SMS</CardTitle>
                <CardDescription className="text-zinc-400 text-xs sm:text-sm">
                  Verify your SMS configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Phone Number</Label>
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Message</Label>
                  <Textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Test message..."
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm min-h-20"
                  />
                </div>
                <Button 
                  onClick={handleSendTestSMS} 
                  disabled={sending || !smsConfig.enabled}
                  className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                >
                  {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test
                </Button>
                {!smsConfig.enabled && (
                  <p className="text-xs text-amber-400">Configure SMS provider first</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Configure Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              SMS Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300 text-sm">Enable SMS</Label>
              <Switch
                checked={smsConfig.enabled}
                onCheckedChange={(checked) => setSmsConfig({...smsConfig, enabled: checked})}
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Provider</Label>
              <Select value={smsConfig.provider} onValueChange={(value) => setSmsConfig({...smsConfig, provider: value})}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Twilio-specific fields */}
            <div>
              <Label className="text-zinc-300 text-sm">Account SID</Label>
              <div className="relative mt-1">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={smsConfig.account_sid || ''}
                  onChange={(e) => setSmsConfig({...smsConfig, account_sid: e.target.value})}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="bg-zinc-800 border-zinc-700 text-white text-sm pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 h-7 w-7 p-0"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-1">Found in Twilio Console Dashboard</p>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Auth Token</Label>
              <div className="relative mt-1">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={smsConfig.auth_token || ''}
                  onChange={(e) => setSmsConfig({...smsConfig, auth_token: e.target.value})}
                  placeholder="Your Auth Token"
                  className="bg-zinc-800 border-zinc-700 text-white text-sm pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 h-7 w-7 p-0"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300 text-sm">Twilio Phone Number</Label>
              <Input
                value={smsConfig.sender_phone}
                onChange={(e) => setSmsConfig({...smsConfig, sender_phone: e.target.value})}
                placeholder="+1234567890"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">Your approved Twilio phone number (E.164 format)</p>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Timezone</Label>
              <Select value={smsConfig.timezone} onValueChange={(value) => setSmsConfig({...smsConfig, timezone: value})}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowConfigDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
